import React, { forwardRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { formatListingDate, formatPrice, formatReturn, isMultibagger, returnBadge, returnTone } from '../../utils/ipoPerformance';

const PILL_TONES = {
  gain: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
  loss: 'bg-rose-50 text-rose-600 ring-rose-200/70',
  flat: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const TEXT_TONES = { gain: 'text-emerald-600', loss: 'text-rose-600', flat: 'text-slate-600' };

const ReturnPill = ({ value, strong = false }) => (
  <span
    className={`inline-flex px-2.5 py-1 rounded-full ring-1 text-[11px] sm:text-xs font-bold tabular-nums whitespace-nowrap ${
      strong ? 'bg-emerald-100 text-emerald-700 ring-emerald-300' : PILL_TONES[returnTone(value)]
    }`}
  >
    {formatReturn(value)}
  </span>
);

const PageButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

export const PerformanceTable = forwardRef(({ rows, page, pageSize, onPageChange, noun, onViewDetail, onViewQuotes, emptyMessage }, ref) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstIndex = rows.length ? (currentPage - 1) * pageSize + 1 : 0;
  const lastIndex = Math.min(currentPage * pageSize, rows.length);

  return (
    <section ref={ref} aria-label={`${noun} IPO performance`} className="scroll-mt-20 bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] sm:text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              {/* Fixed width: long names wrap here instead of forcing the numeric headers onto two lines */}
              <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3.5 pl-4 sm:pl-5 pr-3 w-[180px] min-w-[180px] sm:w-[240px] sm:min-w-[220px]">
                Company Name
              </th>
              <th scope="col" className="py-3.5 px-2.5 whitespace-nowrap">Listed On</th>
              <th scope="col" className="py-3.5 px-2.5 text-right">Issue Price</th>
              <th scope="col" className="py-3.5 px-2.5 text-right">Listing Day Close</th>
              <th scope="col" className="py-3.5 px-2.5 text-right">Listing Day Gain</th>
              <th scope="col" className="py-3.5 px-2.5 text-right">Current Price (CMP)</th>
              <th scope="col" className="py-3.5 px-2.5 text-right">Current Profit / Loss</th>
              <th scope="col" className="py-3.5 pl-2.5 pr-4 sm:pr-5 text-center whitespace-nowrap">Quick Links</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {pageRows.map((row) => {
              const standout = isMultibagger(row);
              const badge = returnBadge(row);
              return (
                <tr key={row.id} className={`group transition-colors ${standout ? 'bg-teal-50/60' : 'hover:bg-slate-50/70'}`}>
                  <td
                    className={`sticky left-0 z-10 py-3.5 pl-4 sm:pl-5 pr-3 ${
                      standout ? 'bg-teal-50' : 'bg-white group-hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1.5 sm:gap-2">
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => onViewDetail(row)}
                          className="text-left font-bold text-[#008080] hover:text-[#006666] hover:underline underline-offset-2"
                        >
                          {row.company}
                        </button>
                        <p className="mt-0.5 text-[11px] text-slate-400">
                          {row.exchanges.join(', ')} • {row.sector}
                        </p>
                      </div>
                      {badge && (
                        <span className="self-start shrink-0 px-1.5 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-700 text-[9px] font-extrabold leading-none whitespace-nowrap">
                          {badge}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3.5 px-2.5 whitespace-nowrap text-slate-500">{formatListingDate(row.listedOn)}</td>
                  <td className="py-3.5 px-2.5 text-right whitespace-nowrap tabular-nums font-semibold text-slate-800">
                    {formatPrice(row.issuePrice)}
                  </td>
                  <td className="py-3.5 px-2.5 text-right whitespace-nowrap tabular-nums text-slate-700">{formatPrice(row.listingClose)}</td>
                  <td className="py-3.5 px-2.5 text-right">
                    <ReturnPill value={row.listingGain} />
                  </td>
                  <td
                    className={`py-3.5 px-2.5 text-right whitespace-nowrap tabular-nums ${
                      standout ? 'font-extrabold text-[#008080]' : 'font-semibold text-slate-800'
                    }`}
                  >
                    {formatPrice(row.cmp)}
                  </td>
                  <td className="py-3.5 px-2.5 text-right whitespace-nowrap">
                    {standout ? (
                      <ReturnPill value={row.currentReturn} strong />
                    ) : (
                      <span className={`font-bold tabular-nums ${TEXT_TONES[returnTone(row.currentReturn)]}`}>
                        {formatReturn(row.currentReturn)}
                      </span>
                    )}
                  </td>
                  <td className="py-3.5 pl-2.5 pr-4 sm:pr-5 text-center whitespace-nowrap text-xs">
                    <button
                      type="button"
                      onClick={() => onViewDetail(row)}
                      aria-label={`IPO Detail: ${row.company}`}
                      className="font-semibold text-[#008080] hover:text-[#006666] hover:underline underline-offset-2"
                    >
                      IPO Detail
                    </button>
                    <span className="mx-1.5 text-slate-300" aria-hidden="true">
                      |
                    </span>
                    <button
                      type="button"
                      onClick={() => onViewQuotes(row)}
                      aria-label={`Quotes: ${row.company}`}
                      className="font-medium text-slate-500 hover:text-slate-800 hover:underline underline-offset-2"
                    >
                      Quotes
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
        <p className="text-xs text-slate-500">
          Showing <strong className="text-slate-800">{firstIndex}</strong> to <strong className="text-slate-800">{lastIndex}</strong> of{' '}
          <strong className="text-slate-800">{rows.length}</strong> {noun} IPOs
        </p>
        {totalPages > 1 && (
          <nav className="flex flex-wrap items-center justify-center gap-1.5" aria-label="Performance table pagination">
            <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} label="Previous page">
              <ArrowLeft className="w-3.5 h-3.5" />
              Prev
            </PageButton>
            {buildPageList(currentPage, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                  className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                    p === currentPage
                      ? 'bg-[#008080] border-[#008080] text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <PageButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} label="Next page">
              Next
              <ArrowRight className="w-3.5 h-3.5" />
            </PageButton>
          </nav>
        )}
      </div>
    </section>
  );
});

PerformanceTable.displayName = 'PerformanceTable';

export default PerformanceTable;
